import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
} from '@mui/material';
import {
  FormatSizeOutlined,
  ContrastOutlined,
  RestartAlt,
  KeyboardOutlined,
  AccessibilityNewOutlined,
  CheckCircleOutline,
  InfoOutlined,
} from '@mui/icons-material';
import ListPageLayout from '../components/shared/list-page-layout';

type FontSize = 'normal' | 'medium' | 'large';

interface AccessibilityViewProps {
  entityName?: string;
  fontSize: FontSize;
  highContrast: boolean;
  onIncreaseFontSize: () => void;
  onDecreaseFontSize: () => void;
  onToggleHighContrast: () => void;
  onResetAccessibility: () => void;
}

export const AccessibilityView: React.FC<AccessibilityViewProps> = ({
  entityName,
  fontSize,
  highContrast,
  onIncreaseFontSize,
  onDecreaseFontSize,
  onToggleHighContrast,
  onResetAccessibility,
}) => {
  const getFontSizeLabel = () => {
    switch (fontSize) {
      case 'normal':
        return 'Normal';
      case 'medium':
        return 'Médio';
      case 'large':
        return 'Grande';
      default:
        return 'Normal';
    }
  };

  const resources = [
    {
      icon: <FormatSizeOutlined sx={{ fontSize: 40 }} />,
      title: 'Ajuste de Fonte',
      description: 'Aumente ou diminua o tamanho do texto para facilitar a leitura. Três níveis disponíveis: Normal, Médio e Grande.',
    },
    {
      icon: <ContrastOutlined sx={{ fontSize: 40 }} />,
      title: 'Alto Contraste',
      description: 'Ative o modo de alto contraste para melhorar a legibilidade e facilitar a distinção entre elementos da página.',
    },
    {
      icon: <KeyboardOutlined sx={{ fontSize: 40 }} />,
      title: 'Navegação por Teclado',
      description: 'Navegue pelo site usando apenas o teclado. Pressione Tab para avançar e Shift+Tab para retroceder entre os elementos.',
    },
    {
      icon: <CheckCircleOutline sx={{ fontSize: 40 }} />,
      title: 'Textos Alternativos',
      description: 'Todas as imagens possuem descrições alternativas para leitores de tela e tecnologias assistivas.',
    },
    {
      icon: <AccessibilityNewOutlined sx={{ fontSize: 40 }} />,
      title: 'ARIA Labels',
      description: 'Elementos interativos possuem rótulos ARIA para melhor compreensão por tecnologias assistivas.',
    },
    {
      icon: <InfoOutlined sx={{ fontSize: 40 }} />,
      title: 'Estrutura Semântica',
      description: 'O site utiliza HTML semântico para facilitar a navegação e compreensão do conteúdo.',
    },
  ];

  const shortcuts = [
    { key: 'Tab', description: 'Avançar para o próximo elemento' },
    { key: 'Shift + Tab', description: 'Retroceder para o elemento anterior' },
    { key: 'Enter', description: 'Ativar links e botões' },
    { key: 'Esc', description: 'Fechar modais e menus' },
    { key: 'Setas', description: 'Navegar em menus e listas' },
    { key: 'Espaço', description: 'Ativar botões e checkboxes' },
  ];

  return (
    <ListPageLayout
      title="Acessibilidade"
      subtitle="Recursos e ferramentas para garantir uma experiência acessível a todos os usuários"
      breadcrumbs={[{ label: 'Acessibilidade' }]}
    >
      {/* Card de Controles */}
      <Card
        elevation={3}
        sx={{
          mb: 4,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <AccessibilityNewOutlined sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="h2">
              Controles de Acessibilidade
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  TAMANHO DA FONTE
                </Typography>
                <Chip
                  label={getFontSizeLabel()}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    mb: 2,
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<FormatSizeOutlined />}
                    onClick={onDecreaseFontSize}
                    disabled={fontSize === 'normal'}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    A-
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<FormatSizeOutlined />}
                    onClick={onIncreaseFontSize}
                    disabled={fontSize === 'large'}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    A+
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  ALTO CONTRASTE
                </Typography>
                <Chip
                  label={highContrast ? 'Ativo' : 'Desativado'}
                  color={highContrast ? 'success' : 'default'}
                  sx={{
                    backgroundColor: highContrast ? '#4caf50' : 'rgba(255, 255, 255, 0.7)',
                    color: highContrast ? 'white' : '#666',
                    fontWeight: 600,
                    mb: 2,
                  }}
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<ContrastOutlined />}
                    onClick={onToggleHighContrast}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    {highContrast ? 'Desativar' : 'Ativar'}
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  REDEFINIR
                </Typography>
                <Chip
                  label="Padrão"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    color: '#666',
                    fontWeight: 600,
                    mb: 2,
                  }}
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<RestartAlt />}
                    onClick={onResetAccessibility}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                    }}
                  >
                    Restaurar
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recursos Disponíveis */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
        Recursos Disponíveis
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resources.map((resource, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                    margin: '0 auto 16px',
                  }}
                >
                  {resource.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {resource.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {resource.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Atalhos de Teclado */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
        Atalhos de Teclado
      </Typography>

      <Card elevation={2} sx={{ mb: 4, backgroundColor: '#f5f7fa' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {shortcuts.map((shortcut, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: index < shortcuts.length - 1 ? '1px solid #e0e0e0' : 'none',
                  }}
                >
                  <Chip
                    label={shortcut.key}
                    sx={{
                      backgroundColor: 'white',
                      border: '2px solid #ddd',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      mr: 2,
                      minWidth: 100,
                    }}
                  />
                  <Typography variant="body2">{shortcut.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Padrões e Diretrizes */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
        Padrões e Diretrizes
      </Typography>

      <Card elevation={2}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            Este portal foi desenvolvido seguindo as diretrizes de acessibilidade estabelecidas pelo{' '}
            <strong>e-MAG (Modelo de Acessibilidade em Governo Eletrônico)</strong> e as recomendações
            internacionais do <strong>WCAG 2.1 (Web Content Accessibility Guidelines)</strong>.
          </Typography>
          <Typography variant="body1" paragraph>
            Nosso compromisso é garantir que todos os cidadãos, independentemente de suas capacidades físicas ou
            cognitivas, possam acessar as informações e serviços disponibilizados neste portal.
          </Typography>
          <Typography variant="body1">
            Caso encontre alguma barreira de acessibilidade ou tenha sugestões de melhorias, entre em contato
            através dos canais de atendimento disponíveis no portal.
          </Typography>
        </CardContent>
      </Card>
    </ListPageLayout>
  );
};
